import { useState, useEffect } from 'react';

/**
 * PWA Install Prompt
 * Shows banner to install app on mobile/desktop
 */
export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      console.log('📱 Install prompt available');
      e.preventDefault();
      setDeferredPrompt(e);

      // Show prompt after 5 seconds
      setTimeout(() => {
        setShowPrompt(true);
      }, 5000);
    };

    // Listen for app installed
    const handleAppInstalled = () => {
      console.log('✅ PWA installed');
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show install prompt
    deferredPrompt.prompt();

    // Wait for user response
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`📱 User response: ${outcome}`);

    if (outcome === 'accepted') {
      console.log('✅ User accepted install');
    } else {
      console.log('❌ User dismissed install');
    }

    // Clear prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Don't show again for this session
    sessionStorage.setItem('pwa-install-dismissed', 'true');
  };

  // Don't show if installed or dismissed
  if (isInstalled || !showPrompt || sessionStorage.getItem('pwa-install-dismissed')) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up">
      <div className="bg-primary-600 text-white rounded-lg shadow-2xl p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="shrink-0 text-3xl">
            📱
          </div>

          {/* Content */}
          <div className="flex-1">
            <h3 className="font-semibold mb-1">Installeer HavunCore</h3>
            <p className="text-sm text-primary-100 mb-3">
              Installeer de app op je apparaat voor snellere toegang en offline gebruik.
            </p>

            <div className="flex gap-2">
              <button
                onClick={handleInstallClick}
                className="flex-1 bg-white text-primary-600 font-medium py-2 px-4 rounded-lg hover:bg-primary-50 transition-colors"
              >
                Installeer
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 text-primary-100 hover:text-white transition-colors"
              >
                Later
              </button>
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="shrink-0 text-primary-200 hover:text-white -mt-1 -mr-1"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

export default InstallPrompt;
